/**
 * Client health, as literal facts (frontend). No composite score: each client
 * carries its last two-way touch, open AR, open deals and next meeting, plus
 * one line saying why it is flagged. Mirrors apps/api domain/clients/client-health.ts.
 */

import type { SourceCoverage } from '@waypoint/shared';
import { supabase } from './supabase';

const API_URL =
  import.meta.env.VITE_API_URL ??
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:8787'
    : 'https://api.waypoint-intel.com');

async function authHeader(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return { Authorization: `Bearer ${session.access_token}` };
}

/** 'crm' = the CRM's own last-activity stamp, shown only when no mailbox touch exists. */
export type TouchChannel = 'email' | 'meeting' | 'call' | 'crm';

export interface ClientFacts {
  id: string;
  name: string;
  source: string;
  basis?: string | null;
  revenueAnnual: number;
  /** null without a connected, synced accounting/payments source: unseen is not zero. */
  arBalance: number | null;
  arBucket: '0-30' | '31-60' | '61-90' | '90+' | 'current' | null;
  /** Last two-way touch: a meeting, a call, or mail they sent. */
  lastTouch: { at: string; channel: TouchChannel } | null;
  /** Dollars; null without a visible accounting/payments source. */
  openAr: { amount: number; count: number; oldestDaysOverdue: number } | null;
  /** Dollars; null without a visible CRM. */
  openDeals: { count: number; total: number } | null;
  nextMeeting: { at: string; title: string | null } | null;
  /** "No two-way touch in 47 days", "$12,400 overdue 63 days"; null when nothing is flagged. */
  whyFlagged: string | null;
  lastActivity: string | null;
  riskFactors: string[];
  /** Churn model overlay (routes/client-health.ts mlOverlay); null until a servable model scored this client. */
  mlChurnProbability?: number | null;
  mlChurnDrivers?: Array<{ label?: string; weight?: number }> | null;
}

export interface ClientHealthResult {
  clients: ClientFacts[];
  summary: {
    totalClients: number;
    /** null when neither email nor billing is connected and synced: nothing could have flagged anyone. */
    flagged: number | null;
    /** null without a connected, synced billing source. */
    openAr: number | null;
  };
  coverage: SourceCoverage;
  generatedAt: string;
}

export async function getClientHealth(): Promise<ClientHealthResult> {
  const headers = await authHeader();
  const res = await fetch(`${API_URL}/v1/client-health`, { headers });
  if (!res.ok) throw new Error(`API ${res.status}: ${(await res.text()).slice(0, 200)}`);
  return (await res.json()) as ClientHealthResult;
}
